import api from './client'

export interface TransactionSummary {
  id: string
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER' | 'REFUND' | 'PERSON_PAYMENT'
  transferDirection?: 'IN' | 'OUT'
  transferId?: string
  partnerAccountName?: string
  financialAccountId?: string
  financialAccountName?: string
  amount: number
  date: string
  description: string
  categoryId?: string
  categoryName?: string
  categoryIcon?: string
  notes?: string
  addedByUserId: string
  attributedToHouseholdMemberId?: string
  attributedMemberName?: string
  isCapitalEvent: boolean
  capitalEventLabel?: string
  createdAt: string
  /** REFUND: the EXPENSE it refunds. INCOME: an optional seller-compensation link. */
  relatedTransactionId?: string
  relatedTransactionDescription?: string
  /** EXPENSE rows only: amount minus refunds already recorded against it. */
  remainingRefundableAmount?: number
  contactId?: string
  contactName?: string
  /** EXPENSE rows only: the part of the bill other people owe back. */
  sharedAmount?: number
  splits?: { contactId: string; contactName: string; amount: number }[]
  settlementId?: string
  /** An EXPENSE paid from a household account that no loan repayment generated. */
  splittable?: boolean
  /** The loan entry (EMI, prepayment, increase) this row belongs to. */
  loanEntryId?: string | null
  /** TRANSFER only: the other leg's account. */
  partnerAccountId?: string | null
  /** TRANSFER only: the goal this earmark contribution belongs to. */
  goalId?: string | null
  goalName?: string | null
  /** Kind of the reminder this came from. Optional: the frontend can deploy before the backend. */
  recurringKind?: 'BILL' | 'SUBSCRIPTION' | 'OTHER' | 'EMI' | 'INCOME' | null
  /** INCOME only: the month (yyyy-MM) this money is meant to cover. Optional: older APIs omit it. */
  spendMonth?: string | null
  /** Multi-account EXPENSE rows only. Optional: older APIs omit it. */
  payments?: PaymentView[]
  /** When this was ticked off against a statement. Optional: older APIs omit it. */
  reconciledAt?: string | null
}

export interface DailyTotal {
  date: string
  spent: number
  income: number
}

export interface TransactionListResponse {
  transactions: TransactionSummary[]
  nextCursor: string | null
  totalIncome: number
  totalExpenses: number
  // Optional: the web app can deploy before the API that adds them.
  /** Net spend, capital events excluded. */
  spent?: number
  /** Income, capital events excluded. */
  income?: number
  capitalSpent?: number
  capitalIncome?: number
  capitalCount?: number
  /** One entry per day of the period; null beyond 62 days. */
  daily?: DailyTotal[] | null
}

export interface TransactionFilters {
  /** "For spending in" months, yyyy-MM: INCOME by the month it covers; overrides the date filters. */
  spendMonthFrom?: string
  spendMonthTo?: string
  month?: string
  /** Single day, yyyy-MM-dd — when present the backend ignores month */
  date?: string
  /** Range start inclusive, yyyy-MM-dd — overrides month/date when combined with dateTo */
  dateFrom?: string
  /** Range end inclusive, yyyy-MM-dd — overrides month/date when combined with dateFrom */
  dateTo?: string
  financialAccountId?: string
  categoryId?: string
  type?: string
  attributedToHouseholdMemberId?: string
  contactId?: string
  goalId?: string
  isCapitalEvent?: boolean
  /** false: only rows not yet ticked off against a statement. */
  reconciled?: boolean
  /** Description contains, 2-100 characters. */
  q?: string
  cursor?: string
  limit?: number
}

export interface PaymentPart {
  financialAccountId: string
  amount: number
}

export interface PaymentView {
  financialAccountId: string
  financialAccountName?: string
  amount: number
}

export interface CreateTransactionPayload {
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER' | 'REFUND'
  financialAccountId?: string
  fromAccountId?: string
  toAccountId?: string
  amount: number
  date: string
  description: string
  categoryId?: string
  notes?: string
  attributedToHouseholdMemberId?: string
  isCapitalEvent?: boolean
  capitalEventLabel?: string
  /**
   * REFUND: the EXPENSE being refunded — omit for a standalone refund (then financialAccountId
   * + categoryId are required instead). INCOME: optional compensation link.
   */
  relatedTransactionId?: string
  splits?: { contactId: string; amount: number }[]
  paidByContactId?: string
  /** EXPENSE only: 2+ parts on distinct accounts adding up to amount; replaces financialAccountId. */
  payments?: PaymentPart[]
  /** INCOME only, yyyy-MM: the date's month or the next. Omit to let the server learn it. */
  spendMonth?: string
}

export interface UpdateTransactionPayload {
  amount?: number
  date?: string
  description?: string
  categoryId?: string | null
  notes?: string | null
  attributedToHouseholdMemberId?: string | null
  /** true removes the member; a null id alone leaves it unchanged. */
  clearAttributedMember?: boolean
  isCapitalEvent?: boolean
  capitalEventLabel?: string | null
  /** Absent = unchanged, [] = clear. */
  splits?: { contactId: string; amount: number }[]
  payments?: PaymentPart[]
  /** INCOME only, yyyy-MM. Absent = unchanged (a moved date carries its offset). */
  spendMonth?: string
}

export interface UpdateTransferPayload {
  amount?: number
  date?: string
  description?: string
}

export function listTransactions(
  filters: TransactionFilters = {}
): Promise<TransactionListResponse> {
  const params: Record<string, string | number> = {}
  if (filters.spendMonthFrom) params.spendMonthFrom = filters.spendMonthFrom
  if (filters.spendMonthTo) params.spendMonthTo = filters.spendMonthTo
  if (filters.month) params.month = filters.month
  if (filters.date) params.date = filters.date
  if (filters.dateFrom) params.dateFrom = filters.dateFrom
  if (filters.dateTo) params.dateTo = filters.dateTo
  if (filters.financialAccountId) params.financialAccountId = filters.financialAccountId
  if (filters.categoryId) params.categoryId = filters.categoryId
  if (filters.type) params.type = filters.type
  if (filters.attributedToHouseholdMemberId) {
    params.attributedToHouseholdMemberId = filters.attributedToHouseholdMemberId
  }
  if (filters.contactId) params.contactId = filters.contactId
  if (filters.goalId) params.goalId = filters.goalId
  if (filters.isCapitalEvent) params.isCapitalEvent = 'true'
  if (filters.reconciled !== undefined) params.reconciled = String(filters.reconciled)
  if (filters.q) params.q = filters.q
  if (filters.cursor) params.cursor = filters.cursor
  if (filters.limit) params.limit = filters.limit
  return api.get('/transactions', { params }).then((r) => r.data)
}

export interface EarmarkGoalRef {
  id: string
  name: string
  earmarkedAmount: number
}

export interface EarmarkWarning {
  shortfall: number
  goals: EarmarkGoalRef[]
}

export interface CreateTransactionResponse {
  earmarkWarning: EarmarkWarning | null
}

export function createTransaction(
  payload: CreateTransactionPayload
): Promise<CreateTransactionResponse> {
  return api.post('/transactions', payload).then((r) => r.data)
}

export function updateTransaction(id: string, payload: UpdateTransactionPayload): Promise<void> {
  return api.patch(`/transactions/${id}`, payload)
}

export function deleteTransaction(id: string): Promise<void> {
  return api.delete(`/transactions/${id}`)
}

export function updateTransfer(transferId: string, payload: UpdateTransferPayload): Promise<void> {
  return api.patch(`/transfers/${transferId}`, payload)
}

export function deleteTransfer(transferId: string): Promise<void> {
  return api.delete(`/transfers/${transferId}`)
}

export interface TransactionSuggestion {
  description: string
  type: 'INCOME' | 'EXPENSE' | 'REFUND' | 'PERSON_PAYMENT'
  /** Null for an expense someone else paid. */
  financialAccountId: string | null
  financialAccountName: string | null
  categoryId: string | null
  categoryName: string | null
  amount: number
  notes: string | null
}

/** The month a new income is likely meant to cover, learned from the last one like it. */
export function fetchSpendMonthSuggestion(params: {
  date: string
  categoryId?: string
  financialAccountId?: string
}): Promise<{ spendMonth: string }> {
  return api.get('/transactions/spend-month-suggestion', { params }).then((r) => r.data)
}

export function suggestTransactions(query: string): Promise<TransactionSuggestion[]> {
  return api.get('/transactions/suggestions', { params: { q: query } }).then((r) => r.data)
}

/** A candidate transaction for the refund/compensation "link to a transaction" picker. */
export interface LinkedTransactionOption {
  id: string
  type: 'INCOME' | 'EXPENSE' | 'TRANSFER' | 'REFUND'
  description: string
  date: string
  amount: number
  financialAccountName: string | null
  categoryName: string | null
  /** Set only for EXPENSE rows: amount minus refunds already recorded against it. */
  remainingRefundableAmount: number | null
  payments?: PaymentView[]
}

export function searchLinkableTransactions(
  query: string,
  options: { type?: string; refundableOnly?: boolean } = {}
): Promise<LinkedTransactionOption[]> {
  return api
    .get('/transactions/link-search', {
      params: {
        q: query,
        ...(options.type ? { type: options.type } : {}),
        ...(options.refundableOnly ? { refundableOnly: true } : {}),
      },
    })
    .then((r) => r.data)
}

export type BulkAction = 'SET_CATEGORY' | 'SET_MEMBER' | 'SET_CAPITAL' | 'SET_RECONCILED' | 'DELETE'

export interface BulkTransactionRequest {
  ids: string[]
  action: BulkAction
  categoryId?: string
  /** SET_MEMBER: null clears the member. */
  memberId?: string | null
  isCapitalEvent?: boolean
  reconciled?: boolean
}

export interface BulkTransactionResponse {
  changed: number
  skipped: { id: string; reason: string }[]
}

/** One change across many rows, all or nothing; rows it doesn't apply to come back as skipped. */
export function bulkTransactions(req: BulkTransactionRequest): Promise<BulkTransactionResponse> {
  return api.post('/transactions/bulk', req).then((r) => r.data)
}
